// Lógica PURA do Feed North (testada em __tests__/feedLogica.test.ts).

export type Anexo = {
  /** Caminho no bucket `feed`: "<post_id>/<arquivo>". */
  caminho: string;
  nome: string;
  tipo: string;
  tamanho: number;
};

export type Post = {
  id: string;
  autor_id: string;
  autor_nome: string;
  texto: string;
  anexos: Anexo[];
  publico: 'todos' | 'equipe';
  equipe_gerente: number | null;
  fixado_em: string | null;
  created_at: string;
  editado_em: string | null;
};

export type Curtida = { post_id: string; user_id: string; user_nome: string };
export type Comentario = {
  id: string;
  post_id: string;
  autor_id: string;
  autor_nome: string;
  texto: string;
  created_at: string;
};

const texto = (v: unknown) => (typeof v === 'string' ? v : '');

/**
 * Linha do banco → Post com todos os campos. O banco não deixa campo nulo
 * (NOT NULL DEFAULT), mas a tela não pode cair se vier linha incompleta
 * (cache, versão velha, teste).
 */
export const normalizarPost = (r: Record<string, unknown>): Post => ({
  id: texto(r.id) || String(r.id ?? ''),
  autor_id: texto(r.autor_id),
  autor_nome: texto(r.autor_nome) || 'North',
  texto: texto(r.texto),
  anexos: Array.isArray(r.anexos) ? (r.anexos as Anexo[]).filter((a) => a && typeof a.caminho === 'string') : [],
  publico: r.publico === 'equipe' ? 'equipe' : 'todos',
  equipe_gerente: r.equipe_gerente == null ? null : Number(r.equipe_gerente),
  fixado_em: texto(r.fixado_em) || null,
  created_at: texto(r.created_at) || new Date(0).toISOString(),
  editado_em: texto(r.editado_em) || null,
});

export const normalizarComentario = (r: Record<string, unknown>): Comentario => ({
  id: String(r.id ?? ''),
  post_id: texto(r.post_id),
  autor_id: texto(r.autor_id),
  autor_nome: texto(r.autor_nome) || 'Usuário',
  texto: texto(r.texto),
  created_at: texto(r.created_at) || new Date(0).toISOString(),
});

export const normalizarCurtida = (r: Record<string, unknown>): Curtida => ({
  post_id: texto(r.post_id),
  user_id: texto(r.user_id),
  user_nome: texto(r.user_nome) || 'Usuário',
});

/** Janela do "não lido": post mais velho que isso não acende o sino. */
export const DIAS_NAO_LIDO = 30;

export const ehImagem = (a: Pick<Anexo, 'tipo' | 'nome'>) =>
  a.tipo.startsWith('image/') || /\.(png|jpe?g|gif|webp|avif)$/i.test(a.nome);

/** Fixados primeiro (o fixado mais recente em cima), depois do mais novo pro mais velho. */
export function ordenarPosts(posts: Post[]): Post[] {
  return [...posts].sort((a, b) => {
    if (a.fixado_em && b.fixado_em) return b.fixado_em.localeCompare(a.fixado_em);
    if (a.fixado_em) return -1;
    if (b.fixado_em) return 1;
    return b.created_at.localeCompare(a.created_at);
  });
}

/**
 * Posts que ainda não vi, do mais novo pro mais velho. Meu próprio post não
 * conta (quem publicou já viu), e post fora da janela também não — quem entra
 * na plataforma hoje não herda meses de "novidade".
 */
export function naoLidos(
  posts: Pick<Post, 'id' | 'autor_id' | 'created_at'>[],
  vistos: Set<string>,
  euId: string | null,
  agora: Date = new Date(),
): string[] {
  const limite = agora.getTime() - DIAS_NAO_LIDO * 864e5;
  return [...posts]
    .filter((p) => !vistos.has(p.id) && p.autor_id !== euId && Date.parse(p.created_at) >= limite)
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
    .map((p) => p.id);
}

/** "agora", "há 5 min", "há 3 h", "ontem", "12 de set." */
export function quando(iso: string, agora: Date = new Date()): string {
  const d = new Date(iso);
  const seg = Math.max(0, (agora.getTime() - d.getTime()) / 1000);
  if (seg < 60) return 'agora';
  if (seg < 3600) return `há ${Math.floor(seg / 60)} min`;
  if (seg < 86400) return `há ${Math.floor(seg / 3600)} h`;
  const ontem = new Date(agora);
  ontem.setDate(ontem.getDate() - 1);
  if (d.toDateString() === ontem.toDateString()) return 'ontem';
  return d.toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'short',
    ...(d.getFullYear() !== agora.getFullYear() ? { year: 'numeric' } : {}),
  });
}

export const tamanhoArquivo = (bytes: number): string =>
  bytes < 1024 ? `${bytes} B` : bytes < 1024 ** 2 ? `${Math.round(bytes / 1024)} KB` : `${(bytes / 1024 ** 2).toFixed(1).replace('.', ',')} MB`;

/** Primeira linha do post, curta — para o sino e o cartão do Início geral. */
export function resumoDoPost(p: Pick<Post, 'texto' | 'anexos'>, max = 90): string {
  const linha = p.texto.trim().split('\n').find((l) => l.trim()) ?? '';
  if (!linha) return p.anexos.length ? `${p.anexos.length} ${p.anexos.length === 1 ? 'arquivo' : 'arquivos'}` : '';
  return linha.length > max ? `${linha.slice(0, max - 1).trimEnd()}…` : linha;
}

/** Texto em pedaços: link vira link, o resto fica texto. */
export function comLinks(texto: string): { texto: string; link: boolean }[] {
  const partes: { texto: string; link: boolean }[] = [];
  const re = /https?:\/\/[^\s<>"')]+/g;
  let ultimo = 0;
  for (const m of texto.matchAll(re)) {
    if (m.index! > ultimo) partes.push({ texto: texto.slice(ultimo, m.index), link: false });
    partes.push({ texto: m[0], link: true });
    ultimo = m.index! + m[0].length;
  }
  if (ultimo < texto.length) partes.push({ texto: texto.slice(ultimo), link: false });
  return partes;
}

/** Nome de arquivo seguro para o bucket (sem acento, espaço nem barra). */
export const nomeNoBucket = (nome: string): string =>
  nome
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^\w.-]+/g, '-')
    .replace(/-+/g, '-')
    .slice(-80);

/** "Você, Lucas e mais 3" — quem curtiu, com "você" primeiro. */
export function quemCurtiu(curtidas: Pick<Curtida, 'user_id' | 'user_nome'>[], euId: string | null): string {
  if (!curtidas.length) return '';
  const eu = curtidas.some((c) => c.user_id === euId);
  const outros = curtidas.filter((c) => c.user_id !== euId).map((c) => c.user_nome.split(' ')[0]);
  const nomes = [...(eu ? ['Você'] : []), ...outros];
  if (nomes.length <= 3) return nomes.length === 1 ? nomes[0] : `${nomes.slice(0, -1).join(', ')} e ${nomes.at(-1)}`;
  return `${nomes.slice(0, 2).join(', ')} e mais ${nomes.length - 2}`;
}
